<template>
  <div class="pa-3 ma-3">
    <v-alert-dialog ref="alert" />

    <!-- Diálogo: Detalle -->
    <v-dialog v-model="dialog_detalle" max-width="860px" persistent>
      <v-card>
        <v-card-title class="headline">Detalles de Solicitud</v-card-title>

        <v-card-text>
          <v-skeleton-loader v-if="loading" type="article, table" />
          <div v-else>
            <v-row>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Folio Solicitud</div>
                <div class="font-weight-medium">{{ detalle?.id_solicitud }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Fecha</div>
                <div class="font-weight-medium">{{ format_datetime(detalle?.created_at) }}</div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Puesto</div>
                <div class="font-weight-medium">{{ detalle?.puesto?.nombre || detalle?.puesto }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Estado</div>
                <div class="d-flex align-center">
                  <v-icon :color="estado_color(detalle?.estado)" small class="mr-2">
                    {{ estado_icon(detalle?.estado) }}
                  </v-icon>
                  <span class="font-weight-medium">{{ estado_label(detalle?.estado) }}</span>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Personas solicitadas</div>
                <div class="font-weight-medium">{{ detalle?.num_solicitados }}</div>
              </v-col>

              <!-- NUEVO: Salario (valor directo) -->
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Salario</div>
                <div class="font-weight-medium">
                  {{ format_currency(detalle?.salario_nivel, detalle?.salario_monto) }}
                </div>
              </v-col>

              <!-- NUEVO: Solicitado por (quién creó la solicitud) -->
              <v-col cols="12" md="6">
                <div class="subtitle-2 grey--text">Solicitado por</div>
                <div class="font-weight-medium">
                  {{ detalle?.solicitado_por || detalle?.creado_por || detalle?.created_by || '—' }}
                </div>
              </v-col>

              <!-- Bloque rechazo (solo lectura) cuando RECHAZADA -->
              <template v-if="detalle?.estado === 'RECHAZADA'">
                <v-col cols="12"><v-divider class="my-4" /></v-col>
                <v-col cols="12">
                  <div class="subtitle-2 grey--text">Motivo del rechazo</div>
                  <div class="font-weight-medium" style="white-space: pre-wrap">
                    {{ detalle?.rechazo_motivo || '—' }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="subtitle-2 grey--text">Rechazada por</div>
                  <div class="font-weight-medium">{{ detalle?.rechazada_por || '—' }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="subtitle-2 grey--text">Fecha rechazo</div>
                  <div class="font-weight-medium">{{ format_datetime(detalle?.rechazada_at) }}</div>
                </v-col>
              </template>

              <!-- Decisión: Aceptar / Rechazar (solo si está PENDIENTE) -->
              <template v-if="detalle?.estado === 'PENDIENTE'">
                <v-col cols="12" class="text-center">
                  <div class="subtitle-1 mb-2">Decisión</div>
                  <div class="d-flex justify-center">
                    <v-btn-toggle
                      v-model="decision_accion"
                      class="justify-center"
                    >
                      <v-btn value="aceptar" outlined>
                        <v-icon left>mdi-check</v-icon>
                        <span class="green--text text--darken-2">Aceptar</span>
                      </v-btn>
                      <v-btn value="rechazar" outlined>
                        <v-icon left>mdi-close-circle</v-icon>
                        <span class="red--text text--darken-2">Rechazar</span>
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </v-col>

                <!-- Motivo solo cuando se elige rechazar -->
                <v-col cols="12" v-if="decision_accion === 'rechazar'">
                  <v-form ref="form_rechazo" v-model="form_rechazo_valido" lazy-validation>
                    <v-textarea
                      v-model="motivo_rechazo"
                      label="Motivo del rechazo"
                      :rules="[rules.required, rules.min10]"
                      auto-grow
                      outlined
                      :rows="1"
                    />
                  </v-form>
                </v-col>
              </template>

              <!-- NUEVO: Expansion panels para Requisitos y Comentario/Justificación -->
              <v-col cols="12">
                <v-expansion-panels multiple>
                  <v-expansion-panel>
                    <v-expansion-panel-header>Requisitos</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      {{ detalle?.requisitos || '—' }}
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>Comentario / Justificación</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      {{ detalle?.comentario || '—' }}
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <!-- Footer: solo Cancelar / Confirmar -->
        <v-card-actions v-if="detalle?.estado === 'PENDIENTE'">
          <v-spacer />
          <v-btn text color="green" @click="cerrar_detalle">Cancelar</v-btn>
          <v-btn
            color="red"
            text
            :disabled="decision_accion == null || (decision_accion == 'rechazar' && (motivo_rechazo == null || motivo_rechazo.length < 10))"
            @click="confirmar_accion"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-spacer />
          <v-btn text color="green" @click="cerrar_detalle">Cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tabla -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          class="elevation-1"
          :items-per-page="10"
        >
          <template v-slot:[`item.puesto`]="{ item }">
            {{ item.puesto?.nombre || item.puesto }}
          </template>

          <template v-slot:[`item.estado`]="{ item }">
            <div class="d-flex align-center justify-center">
              <v-tooltip bottom color="black">
                <template v-slot:activator="{ on }">
                  <div v-on="on" class="d-flex align-center">
                    <v-icon :color="estado_color(item.estado)" small class="mr-2">
                      {{ estado_icon(item.estado) }}
                    </v-icon>
                    <span>{{ estado_label(item.estado) }}</span>
                  </div>
                </template>
                <span>{{ estado_tooltip(item.estado) }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ format_datetime(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center justify-center">
              <v-icon
                small
                class="mr-2"
                @click="ver_detalle(item)"
              >
                {{ action_icon(item.estado) }}
              </v-icon>
            </div>
          </template>

          <template v-slot:no-data>
            <v-alert type="info" outlined>No hay solicitudes</v-alert>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios'
import VAlertDialog from '@/components/VAlertDialog.vue'

export default {
  name: 'PanelAutorizacionVacantes',
  components: { VAlertDialog },
  data: () => ({
    loading: false,
    dialog_detalle: false,
    detalle: null,

    // Toggle de decisión en estado PENDIENTE
    decision_accion: null, // 'aceptar' | 'rechazar' | null
    motivo_rechazo: '',
    form_rechazo_valido: false,
    loading_accion: false,

    headers: [
      { text: 'Folio', value: 'id_solicitud', align: 'center', width: '10%' },
      { text: 'Fecha', value: 'created_at', align: 'center', width: '20%' },
      { text: 'Puesto', value: 'puesto', align: 'center', width: '30%' },
      { text: '# Personas', value: 'num_solicitados', align: 'center', width: '10%' },
      { text: 'Estado', value: 'estado', align: 'center', width: '20%' },
      { text: 'Acciones', value: 'actions', align: 'center', width: '10%', sortable: false },
    ],
    items: [],

    rules: {
      required: v => !!(v && String(v).trim().length) || 'Campo requerido',
      min10: v => (v && String(v).trim().length >= 10) || 'Mínimo 10 caracteres',
    },
  }),
  mounted() {
    this.cargar()
  },
  methods: {
    async cargar() {
      this.loading = true
      try {
        const { data } = await axios.get('/rrhh/vacantes/solicitudes')
        this.items = Array.isArray(data) && data.length ? data : this.mock_ejemplos()
      } catch {
        this.items = this.mock_ejemplos()
      } finally {
        this.loading = false
      }
    },

    ver_detalle(item) {
      this.detalle = { ...item }
      this.decision_accion = null
      this.motivo_rechazo = ''
      this.dialog_detalle = true
    },
    cerrar_detalle() {
      this.dialog_detalle = false
      this.detalle = null
      this.decision_accion = null
      this.motivo_rechazo = ''
    },

    async confirmar_accion() {
      if (!this.detalle) return

      // Si NO está pendiente, solo cerrar
      if (this.detalle.estado !== 'PENDIENTE') {
        this.cerrar_detalle()
        return
      }

      if (!this.decision_accion) return

      this.loading_accion = true
      try {
        if (this.decision_accion === 'aceptar') {
          await axios.post(`/rrhh/vacantes/${this.detalle.id_solicitud}/aprobar`)
          this.$refs.alert.setMessage('Éxito', 'La solicitud fue autorizada correctamente.')
          this.$refs.alert.open()
          await this.cargar()
          this.cerrar_detalle()
        } else {
          // Rechazar
          if (!this.$refs.form_rechazo || !(await this.$refs.form_rechazo.validate())) {
            this.loading_accion = false
            return
          }
          await axios.post(`/rrhh/vacantes/${this.detalle.id_solicitud}/rechazar`, {
            motivo: this.motivo_rechazo,
          })
          this.$refs.alert.setMessage('Listo', 'La solicitud fue rechazada.')
          this.$refs.alert.open()
          await this.cargar()
          this.cerrar_detalle()
        }
      } catch (err) {
        console.log(err);
        this.cerrar_detalle();
      } finally {
        this.loading_accion = false
      }
    },

    // === Estado (icono + color + etiqueta + tooltip)
    estado_icon(estado) {
      const e = (estado || '').toUpperCase()
      if (e.includes('PEND')) return 'mdi-timer-sand'
      if (e.includes('AUTORIZ')) return 'mdi-check-circle'
      if (e.includes('RECHAZ')) return 'mdi-close-circle'
      if (e.includes('CANCEL')) return 'mdi-cancel'
      if (e.includes('RECLUTA')) return 'mdi-account-search'
      if (e.includes('FINAL') || e.includes('FINZA')) return 'mdi-flag-checkered'
      return 'mdi-help-circle'
    },
    estado_color(estado) {
      const e = (estado || '').toUpperCase()
      if (e.includes('PEND')) return 'orange darken-2'
      if (e.includes('AUTORIZ')) return 'green'
      if (e.includes('RECHAZ')) return 'red darken-2'
      if (e.includes('CANCEL')) return 'grey'
      if (e.includes('RECLUTA')) return 'indigo'
      if (e.includes('FINAL') || e.includes('FINZA')) return 'teal'
      return 'blue-grey'
    },
    estado_label(estado) {
      const map = {
        PENDIENTE: 'Pendiente',
        AUTORIZADA: 'Autorizada',
        RECHAZADA: 'Rechazada',
        CANCELADA: 'Cancelada',
        'EN RECLUTAMIENTO': 'En Reclutamiento',
        FINALIZADA: 'Finalizada',
        FINZALIZADA: 'Finzalizada',
      }
      return map[estado] || estado
    },
    estado_tooltip(estado) {
      const e = (estado || '').toUpperCase()
      if (e.includes('PEND')) return 'En espera de autorización.'
      if (e.includes('AUTORIZ')) return 'Aprobada; lista para iniciar reclutamiento.'
      if (e.includes('RECLUTA')) return 'Reclutamiento en curso; ya hay postulantes.'
      if (e.includes('FINAL') || e.includes('FINZA')) return 'Vacantes cubiertas.'
      if (e.includes('RECHAZ')) return 'Solicitud rechazada; consulta el motivo en detalles.'
      if (e.includes('CANCEL')) return 'Solicitud cancelada por el solicitante o el sistema.'
      return 'Estado desconocido.'
    },

    format_datetime(dt) {
      if (!dt) return ''
      const d = new Date(dt)
      if (isNaN(d.getTime())) return dt
      const pad = n => String(n).padStart(2, '0')
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${d.toLocaleTimeString()}`
    },

    // NUEVO: Formato de moneda para salario
    format_currency(nivel, monto) {
      // Permite llamar con un solo argumento (solo el monto)
      const num = monto ?? nivel;
      const level = monto ? nivel : null;

      if (num === null || num === undefined || isNaN(Number(num))) return '—';

      const cantidad = Number(num).toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2
      });

      if (level && /^[ABC]$/i.test(level)) {
        return `${level.toUpperCase()} — ${cantidad}`;
      }
      return cantidad;
    },


    // Icono de acción en tabla
    action_icon(estado) {
      const e = (estado || '').toUpperCase()
      if (e.includes('PEND')) return 'mdi-clipboard-clock-outline'
      return 'mdi-eye'
    },

    // Ejemplos
    mock_ejemplos() {
      const ahora = new Date()
      const h = ms => new Date(ahora.getTime() - ms).toISOString()

      return [
        {
          id_solicitud: 'VAC-2002',
          created_at: h(0.5 * 24 * 3600 * 1000),
          puesto: { nombre: 'Médico general' },
          num_solicitados: 3,
          num_contratados: 0,
          comentario: 'Nueva clínica en operación.',
          requisitos: 'Cédula profesional y experiencia en consulta.',
          salario_nivel: 'B',
          salario_monto: 16000,
          solicitado_por: 'María López',
          estado: 'AUTORIZADA'
        },
        {
          id_solicitud: 'VAC-2001',
          created_at: h(1 * 3600 * 1000),
          puesto: { nombre: 'Auxiliar de recursos humanos' },
          num_solicitados: 2,
          comentario: 'Cobertura por crecimiento.',
          requisitos: 'Licenciatura en curso. Manejo de Excel.',
          salario_nivel: 'A',
          salario_monto: 12000,
          creado_por: 'Juan Pérez',
          estado: 'PENDIENTE'
        },
        {
          id_solicitud: 'VAC-2005',
          created_at: h(6 * 24 * 3600 * 1000),
          puesto: { nombre: 'Técnico radiólogo' },
          num_solicitados: 1,
          comentario: 'Sustitución por baja.',
          requisitos: 'Conocimientos en Rayos X y tomografía.',
          salario_nivel: 'B',
          salario_monto: 16000,
          created_by: 'Sofía Díaz',
          estado: 'RECHAZADA',
          rechazo_motivo: 'Sin presupuesto este trimestre.',
          rechazada_por: 'Dirección',
          rechazada_at: h(5.5 * 24 * 3600 * 1000)
        },
        {
          id_solicitud: 'VAC-2006',
          created_at: h(12 * 24 * 3600 * 1000),
          puesto: { nombre: 'Auxiliar de comunicación' },
          num_solicitados: 1,
          comentario: 'Campaña regional.',
          requisitos: 'Edición básica, redacción, redes sociales.',
          salario_nivel: 'A',
          salario_monto: 12000,
          solicitado_por: 'Carla Ramírez',
          estado: 'CANCELADA'
        },
      ]
    },

  },
}
</script>
